import { AddRounded, ArrowDropDown, CloseRounded, WarningAmberRounded } from '@mui/icons-material';
import { Box, SxProps, Theme, Tooltip } from '@mui/material';
import { FC, MouseEvent, ReactElement, ReactNode, memo, useMemo } from 'react';
import ReactGA from 'react-ga4';
import { buildCapacityTooltipRows } from 'src/components/dispatch/capacityTooltip';
import { DailyVehicleMetric, NO_HIDDEN_DAILY_METRICS } from 'src/components/dispatch/dailyVehicleMetrics';
import { countDeliveryDestinations } from 'src/components/dispatch/deliveryDestinationCount';
import { loadWeightValueText, quantityValueText, volumeValueText } from 'src/components/dispatch/metricText';
import { countPickups } from 'src/components/dispatch/pickupCount';
import { formatShiftDateLabel } from 'src/components/dispatch/shiftDateLabel';
import {
  DisplayMetric,
  effectiveDisplayMetrics,
  isValueMetric,
  ValueMetric,
} from 'src/components/dispatch/unassignedDisplay/displayMetricKeys';
import { buildVehicleAlert } from 'src/components/dispatch/vehicleAlertReasons';
import {
  CAPACITY_BAR_HEIGHT,
  CAPACITY_BAR_INSET,
  capacityBarLeftInset,
  DRAG_HANDLE_WIDTH,
  capacityBarTrackWidth,
  capacityBarWidth,
  fieldBorderStyle,
  NEWLY_ASSIGNED_BG,
  PLATE_NUMBER_MIN_WIDTH,
  VEHICLE_CARD_HEIGHT,
  VEHICLE_CARD_PADDING_BOTTOM,
  VEHICLE_CARD_PADDING_TOP,
} from 'src/components/dispatch/vehicleCardStyles';
import { LoadTotals, ZERO_LOAD, buildOverloadFlags } from 'src/components/dispatch/vehicleLoad';
import { DRIVER_UNSET_LABEL, unsetColorFor } from 'src/constants/assignmentLabels';
import { DispatchCard, Vehicle } from 'src/entities/dispatch';
import { formatLaborHours } from 'src/utils/formatLaborHours';

import { VehicleTariffTotals } from './buildGrossProfitByTruckId';


/**
 * 車番・ドライバーのように「押すと選び直せる」欄の見た目。
 *
 * サンプルは入力欄の体裁になっている。素のテキストだと変更できることが伝わらず、
 * hover して下線が出るまで気づけない。押せるときだけ枠を出す（readOnly の画面では
 * 枠が並ぶだけで押せず、かえって紛らわしいため）。
 */
const editableFieldSx = {
  px: 0.75,
  py: '1px',
  // サンプル lovable の欄に寄せて角を強めに丸める（3px では四角に見える）
  borderRadius: 1.5,
  border: '1px solid hsl(var(--border))',
  bgcolor: 'hsl(var(--background))',
  cursor: 'pointer',
  transition: 'border-color 0.15s, background-color 0.15s',
  '&:hover': { borderColor: 'hsl(var(--primary))', bgcolor: 'hsl(var(--primary) / 0.04)' },
} as const;

/** 値の有無で枠線を出し分ける。判定は fieldBorderStyle に閉じ込める */
const fieldSxFor = (value: string | null | undefined) => ({
  ...editableFieldSx,
  borderStyle: fieldBorderStyle(value),
});

/** 1 段目の右端に置く小バッジ（車両タイプ・納品先数）。名前が伸びても潰れないよう縮まない */
const metaBadgeSx = {
  flexShrink: 0,
  px: 0.5,
  borderRadius: 0.5,
  fontSize: 9,
  fontWeight: 700,
  lineHeight: 1.6,
  bgcolor: 'hsl(var(--secondary))',
  color: 'hsl(var(--muted-foreground))',
} as const;

/**
 * 「積込3」「納品2」のような件数タグ。
 *
 * 幅が足りないとき、以前は省略記号で「納…」となり、肝心の件数が消えていた
 * （指摘 2026/08/19）。件数と 1 文字目は縮めず、ラベルの 2 文字目だけを削る
 * （狭いときは「納2」になる）。幅の判定はレイアウトに任せる（実測すると
 * 描画のたびに測り直しになり、行の幅が変わるたびにちらつく）。
 */
const CountTag: FC<{
  testId: string;
  label: string;
  count: number;
  sx: SxProps<Theme>;
}> = ({ testId, label, count, sx }) => (
  <Box
    component="span"
    data-testid={testId}
    sx={{
      ...metaBadgeSx,
      display: 'inline-flex',
      alignItems: 'center',
      flexShrink: 1,
      minWidth: 0,
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      ...sx,
    }}
  >
    <Box component="span" sx={{ flexShrink: 0 }}>{label.slice(0, 1)}</Box>
    <Box
      component="span"
      data-testid={`${testId}-label-tail`}
      sx={{ flexShrink: 1, minWidth: 0, overflow: 'hidden' }}
    >
      {label.slice(1)}
    </Box>
    <Box component="span" data-testid={`${testId}-count`} sx={{ flexShrink: 0 }}>{count}</Box>
  </Box>
);

interface VehicleInfoProps {
  vehicle: Vehicle;
  cards: DispatchCard[];
  /**
   * カードの高さ。未指定なら既定の `VEHICLE_CARD_HEIGHT`（カード配車・地図）。
   * 長距離配車だけ、1 行の外形（116px）から逆算した値を渡す。
   */
  cardHeight?: number;
  /**
   * この回転の積載量（同時に積んでいる量の最大）。勤務全体の operation を走査しないと
   * 求まらないので、`buildPeakLoadByRowId` が作った値を行から受け取る。未指定なら 0。
   */
  load?: LoadTotals;
  rotationNumber: number;
  enabled: boolean;
  driverName?: string;
  /**
   * 行の勤務時間。1 台に複数のドライバーが同じ時間帯で乗る勤務では、車両単位の代表値だと
   * 全行が 1 人目の勤務になる。undefined なら車両単位、null なら勤務時間を出さない。
   */
  driverShift?: Vehicle['driverShift'];
  /**
   * 表示中の日 ('YYYY-MM-DD')。この日と違う日に始まる勤務には日付を付ける。
   * 未指定なら日をまたぐ勤務にだけ付ける。
   */
  baseDay?: string;
  /** 行の車番 (shift_trucks.truck_number)。未指定なら車両単位の優先順位で決めた値 */
  plateNumber?: string;
  displayMetrics?: Set<DisplayMetric>;
  /** この行 (回転) のコース番号 (log_web_issues#352)。無ければバッジを出さない */
  courseNumber?: string;
  /** この rotation が最終回転の場合 true。回転追加ボタンを表示 */
  canAddRound?: boolean;
  /** 2回転目以降で true。削除ボタンを表示 */
  canDeleteRound?: boolean;
  onAddRound?: () => void;
  onDeleteRound?: () => void;
  /** 指定時は回転削除ボタンを disabled にし、素の title 属性で理由を出す（Task 10 #2）。 */
  deleteRoundDisabledReason?: string;
  /** 車両名 (truck) クリックで select menu を開く */
  onTruckClick?: (anchor: HTMLElement) => void;
  /** 車番クリックで編集する */
  onPlateNumberClick?: (anchor: HTMLElement) => void;
  /** ドライバー名クリックで select menu を開く */
  onDriverClick?: (anchor: HTMLElement) => void;
  /** この車両に配車が 1 件も無いか。true で「計画無」バッジを出す。状態表示なので readOnly でも出す */
  noPlan?: boolean;
  /** 「計画無」バッジの × 。未指定なら × を出さない（消す手段が無い画面でボタンだけ出さない） */
  onDeleteShift?: () => void;
  /** 指定時は × を disabled にし、素の title 属性で理由を出す */
  deleteShiftDisabledReason?: string;
  /** false で回転追加/削除ボタンを隠す。default true（convenience 現状維持）。readOnly とは独立。 */
  rotationEditable?: boolean;
  /** true でこの shift_truck が配車確定済み。🔒確定 バッジを表示する。状態表示なので readOnly でも出す。default false */
  fixed?: boolean;
  /** 車両カラー (hex)。指定時のみ行先頭に縦カラーバーを表示する。default undefined = 非表示 (従来挙動) */
  barColor?: string;
  /** この行に出す運賃 (請求 / 支払 / 粗利)。null / 未指定で非表示。車両合計を 1 回転目の行にだけ渡す想定。 */
  tariffTotals?: VehicleTariffTotals | null;
  /** 積載以外の制約違反。積載超過と合わせて 1 つの警告バッジにまとめる */
  constraintAlerts?: readonly string[];
  /**
   * 「1 日の 1 回転」に紐づく値のうち隠すもの。既定は空 = すべて出す
   * （= /map・カード型配車は渡さないままで現状どおり）。
   *
   * 隠す理由と項目の内訳は `dailyVehicleMetrics.ts`。
   */
  hiddenDailyMetrics?: ReadonlySet<DailyVehicleMetric>;
  /**
   * 直前の自動配車・シナリオ配車でこの車両に新しく案件が入ったか。true で背景を青くする。
   * 行側の Box にも同じ色を当てているが、このカードが行を覆う大きさのため
   * 画面に出るのはこちらの色。
   */
  highlightNewlyAssigned?: boolean;
  /**
   * 左端に置くドラッグハンドル / 展開トグル。
   * 並びは ハンドル → 色 → 展開 で、いずれもカードの中（背景を共有する位置）に入る。
   * 掴む・開くの実体は呼び出し側が持つ（dnd-kit の listeners、展開 state）ため要素で受ける。
   */
  dragHandle?: ReactNode;
  expandToggle?: ReactNode;
  /** 縦カラーバー クリックで色ピッカーを開く */
  onColorBarClick?: (anchor: HTMLElement) => void;
  /** 指定時は縦カラーバーを disabled にし、素の title 属性で理由を出す（Task 10 #7）。 */
  colorDisabledReason?: string;
  /**
   * GA の screen_name。**必須**。/map とカード配車で同じカードを使うため、既定値を持たせると
   * /map の操作が「カード配車」として静かに記録される（P3-a で全境界について決めた契約）。
   */
  screenName: string;
}

export const VehicleInfoView: FC<VehicleInfoProps> = memo(({
  vehicle,
  cards,
  cardHeight = VEHICLE_CARD_HEIGHT,
  load = ZERO_LOAD,
  rotationNumber,
  enabled,
  driverName,
  driverShift: driverShiftOverride,
  baseDay,
  plateNumber: plateNumberOverride,
  displayMetrics,
  courseNumber,
  canAddRound = false,
  canDeleteRound = false,
  onAddRound,
  onDeleteRound,
  deleteRoundDisabledReason,
  onTruckClick,
  onPlateNumberClick,
  onDriverClick,
  rotationEditable = true,
  fixed = false,
  barColor,
  onColorBarClick,
  colorDisabledReason,
  dragHandle,
  expandToggle,
  tariffTotals,
  constraintAlerts,
  hiddenDailyMetrics = NO_HIDDEN_DAILY_METRICS,
  highlightNewlyAssigned = false,
  screenName,
  noPlan = false,
  onDeleteShift,
  deleteShiftDisabledReason,
}) => {
  // displayMetrics 未指定時の保険として quantity を既定にする（prop 自体が渡らない
  // 呼び出し元向け。契約上は effectiveDisplayMetrics が下記で quantity を保証するため、
  // 通常の呼び出し経路ではここに頼らない）。
  const effectiveMetrics = displayMetrics ?? new Set<DisplayMetric>(['quantity']);
  // 供給側 (Map/ConvenienceDispatches の各 Presenter) は displayMetricSet 生成時に既に
  // effectiveDisplayMetrics を適用済みのため、ここでの適用は二重適用になる
  // (effectiveDisplayMetrics は冪等なので動作は正しい)。displayMetrics prop は optional
  // で、prop 未指定・供給側で実効化していない呼び出し元がありうるための保険として残す。
  // 表示情報 (origin_dest 等) を末尾に入れると最終的な else 枝に落ちて volume 扱いされる
  // 不具合を防ぐため、ValueMetric だけで activeMetric を決める。チェックされた ValueMetric
  // の最後 (= 最後にトグルされたもの) を採用。
  // 実効集合を 1 度だけ作って以降はこれを見る。生の prop を見ると、保存値に
  // 車両タグが無いとき既定（納品タグ・車両タイプ・粗利）が効かない
  const effectiveList = effectiveDisplayMetrics([...effectiveMetrics]);
  const effectiveSet = new Set(effectiveList);
  const valueMetricsArray = effectiveList.filter(isValueMetric);
  const activeMetric: ValueMetric = valueMetricsArray[valueMetricsArray.length - 1];

  // 粗利は車両合計。呼び出し側が 1 回転目の行にだけ値を渡す（全行に出すと
  // 同じ数字が並び「回転ごとの値」と誤読されるため）。
  // 車両リスト表示タグ（サンプル MapPage.tsx:1144-1148）
  const showVehicleTypeTag = effectiveSet.has('vehicleTypeTag');
  // コース番号（サンプル VehicleInfo.tsx:204-225。1 行目の先頭の黒地バッジ）
  const showCourseNumber = effectiveSet.has('courseNumber') && !!courseNumber;
  // 表示項目で ON でも、日次の値として隠されていれば出さない（長距離はレーンを
  // 代表行 1 本に畳んで渡すので、地点数がレーン全体の数にならない）
  const showDeliveryTag = effectiveSet.has('deliveryTag') && !hiddenDailyMetrics.has('deliveryTag');
  const showPickupTag = effectiveSet.has('pickupTag') && !hiddenDailyMetrics.has('pickupTag');

  // 日次の値の出し分け。判定を 1 か所に集めて、描画側は boolean だけを見る
  const showCapacity = !hiddenDailyMetrics.has('capacity');
  const showDriverShift = !hiddenDailyMetrics.has('driverShift');
  const showRotation = !hiddenDailyMetrics.has('rotation');

  const grossProfitYen = tariffTotals?.grossProfitYen ?? null;
  const showGrossProfit = !hiddenDailyMetrics.has('grossProfit')
    && effectiveSet.has('grossProfit') && grossProfitYen != null;
  // Intl の currency 書式は全角￥(-￥5,000) になるため、半角¥で自前に組む。
  const yen = (value: number) => `${value < 0 ? '-' : ''}¥${Math.abs(value).toLocaleString('ja-JP')}`;
  const grossProfitText = showGrossProfit ? `粗利 ${yen(grossProfitYen)}` : '';

  // メトリクスごとに実データで current / max を取得。truck マスタの値が無ければ null
  // (= キャパ不明扱い → 超過判定・利用率計算の対象外)。
  const maxCases = vehicle.maxCapacity;
  const maxKg = vehicle.maxCapacityKg ?? null;
  const maxM3 = vehicle.maxCapacityM3 ?? null;

  const currentValue =
    activeMetric === 'weight' ? load.kg : activeMetric === 'volume' ? load.m3 : load.cases;
  const maxValue: number | null = activeMetric === 'weight' ? maxKg : activeMetric === 'volume' ? maxM3 : maxCases;
  // ゲージと数値の色は「今見ている項目」の超過で決める（見えていない項目で赤くしない）
  const isOverloaded = maxValue != null && maxValue > 0 && currentValue > maxValue;
  // 警告バッジは表示項目に依らず 3 つとも見る。配車結果が違反している事実は表示設定で
  // 変わらない（数量を見ているときに重量超過が消えると違反に気づけない）。
  // 判定は「制約違反のある車両のみ」の絞り込みと同じ関数を使う
  const overloads = buildOverloadFlags(vehicle, load);
  const utilization = maxValue != null && maxValue > 0 ? (currentValue / maxValue) * 100 : 0;
  const gaugeWidth = enabled ? Math.min(Math.max(utilization, 0), 100) : 0;
  // バーは 1 本しか無いので、選択中の残りの項目は hover で出す (log_web_issues#295)。
  // 1 つだけ選んでいるときは空配列 = バーの数値と同じ情報しか出ないためツールチップ自体を出さない。
  // Why not useMemo: 依存の load・valueMetricsArray は上の既存コードが毎レンダー作り直して
  // いるので、ここだけ包んでも memo が当たらない。当てるにはその 2 つ（と activeMetric /
  // effectiveSet）の作り直しが要る。この 1 行を包んでも描画コストは変わらないので、
  // メモ化するなら実測を伴う別変更として行う
  const capacityTooltipRows = buildCapacityTooltipRows(valueMetricsArray, load, vehicle);
  const currentDriverName = driverName ?? vehicle.driverName ?? '';
  // この回転で回る納品先の数（伝票数ではなく地点数）
  const destinationCount = useMemo(() => countDeliveryDestinations(cards), [cards]);
  // この回転で積みに行く地点の数
  const pickupCount = useMemo(() => countPickups(cards), [cards]);
  // ESLint typed rules が JSX 直下の truthy narrow を見落とすことがあるので
  // 事前に抜き出して narrow を確実に効かせる。
  const driverShift = (driverShiftOverride !== undefined ? driverShiftOverride : vehicle.driverShift) ?? null;
  const plateNumber = plateNumberOverride ?? vehicle.plateNumber;
  // 労働可能時間はツールチップにのみ出す（本文の稼働時間はサンプル準拠で変えない）。
  // 空文字なら「・労働可能」ごと落として「・」だけが残るのを防ぐ。
  const laborHours = formatLaborHours(driverShift?.workingAvailableSeconds);
  const shiftDateLabel = driverShift ? formatShiftDateLabel(driverShift, baseDay) : null;
  const shiftTooltip = shiftDateLabel
    ? `勤務時間 ${shiftDateLabel.range}${laborHours ? `・労働可能 ${laborHours}` : ''}`
    : '';

  // 超過時もカード全体は赤くしない (白文字との相性が悪く読みづらいため)。
  // 強調はバー (status-error-accent) と数字テキスト (status-error) と警告バッジで行う。
  //
  // Why not 反映中の行を塗る: 車番やドライバーを変えたあと refetch を待つあいだ緑に
  // 塗っていたが、色が強すぎて画面が読めないとの指摘 (2026/08/20)。反映中であることは
  // /map と同じく右下のトーストで伝える。
  const wrapperBg = !enabled
    ? 'hsl(var(--secondary) / 0.35)'
    : highlightNewlyAssigned
      ? NEWLY_ASSIGNED_BG
      : 'hsl(var(--surface-vehicle) / 0.55)';

  // 積載超過と制約違反を 1 つのバッジにまとめる (サンプル VehicleInfo.tsx:591-635)
  const alert = buildVehicleAlert({ enabled, overloads, constraintAlerts });

  const showAddRoundButton = showRotation && rotationEditable && canAddRound && enabled;

  // 容量バーの左端は本文カラムの開始位置。ツール群の内容は画面ごとに違うので実測値を
  // 固定せず、いま描いているものから組み立てる（カード配車では色バーと展開トグルが無い）
  const barLeftInset = capacityBarLeftInset({
    hasColorBar: !!barColor,
    hasExpandToggle: !!expandToggle,
  });

  // 見た目は粗利の内訳ツールチップと同じ流儀 (11px・項目名 … 値 の 2 段組)
  const capacityTooltip = (
    <Box sx={{ py: 0.25, minWidth: 140 }} data-testid="capacity-breakdown">
      {capacityTooltipRows.map((row) => (
        <Box
          key={row.key}
          data-testid="capacity-tooltip-row"
          sx={{ display: 'flex', alignItems: 'baseline', gap: 1, fontSize: 11, lineHeight: 1.6 }}
        >
          <Box component="span" sx={{ flexShrink: 0 }}>{row.label}</Box>
          <Box
            component="span"
            sx={{ ml: 'auto', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
          >
            {row.valueText}
          </Box>
          {/* 率の桁 (2〜3 桁) で値の右端がずれないよう幅を確保する */}
          <Box
            component="span"
            sx={{ flexShrink: 0, minWidth: 32, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
          >
            {row.percentText}
          </Box>
        </Box>
      ))}
    </Box>
  );

  /**
   * 容量の数値とバーに同じツールチップを付ける。両者は DOM 上で離れている
   * (バーはカードを基準にした絶対配置) ので、1 つの Tooltip では包めない。
   */
  const withCapacityTooltip = (node: ReactElement) => (
    capacityTooltipRows.length === 0 ? node : (
      <Tooltip
        title={capacityTooltip}
        placement="top" arrow
        enterDelay={400} enterNextDelay={400}
        leaveDelay={0} disableInteractive
      >
        {node}
      </Tooltip>
    )
  );

  const stopPropagation = (e: MouseEvent) => e.stopPropagation();

  return (
    <Box
      data-testid="vehicle-card"
      sx={{
        height: cardHeight,
        // 左カラムいっぱいに広げる (サンプル route-planner の fillWidth と同じ)。
        // 固定幅 (304) だと左カラム幅 (/map 360・カード配車 320) との差がそのまま
        // 右側の空白になり、粗利や稼働時間が入る余地を捨てていた。
        width: '100%',
        minWidth: 0,
        // 粗利 (下記) を絶対配置で乗せるための基準。
        position: 'relative',
        px: 1.5,
        pt: `${VEHICLE_CARD_PADDING_TOP}px`,
        // 下端に絶対配置した容量バー (bottom:3 / height:5) の帯を空ける
        pb: `${VEHICLE_CARD_PADDING_BOTTOM}px`,
        transition: 'background-color 200ms',
        bgcolor: wrapperBg,
        opacity: enabled ? 1 : 0.75,
      }}
    >
      <Box sx={{ display: 'flex', height: '100%', alignItems: 'flex-start', gap: 1 }}>
        {/*
          左端のツール群は ハンドル → 色 → 展開 の順（掴む → 色を変える → 開く）。
          カードの外に出すと行の背景と分離して「どの車両のものか」が曖昧になるため、
          カードの中に置いて背景を共有する。
        */}
        {/* ハンドル・色バー・展開トグルが全部無い行でも枠は描く。消すとその行だけ
            本文カラムが左に寄り、行頭と容量バーの左端が他の行とずれる */}
        <Box
          data-testid="vehicle-tools"
          sx={{ display: 'flex', alignSelf: 'stretch', alignItems: 'center', gap: 0.25, flexShrink: 0, ml: -0.75 }}
          onClick={stopPropagation}
        >
          {/* 2 回転目以降はハンドルを出さないが、場所は空ける。
              詰めると色バーの左位置が 1 回転目とずれて行頭が階段状になる */}
          {dragHandle ?? <Box sx={{ width: DRAG_HANDLE_WIDTH, flexShrink: 0 }} aria-hidden="true" />}
          {barColor && (
            // colorDisabledReason は素の title 属性で出す (disabled 要素は MUI Tooltip の
            // hover を拾えないため。ConvenienceDispatchToolbar.tsx:551 と同じ規則)。
            <Box
              component={onColorBarClick ? 'button' : 'div'}
              type={onColorBarClick ? 'button' : undefined}
              aria-label={onColorBarClick ? '車両色を変更' : undefined}
              disabled={onColorBarClick && !!colorDisabledReason ? true : undefined}
              title={onColorBarClick ? colorDisabledReason : undefined}
              onClick={onColorBarClick && !colorDisabledReason ? (e: MouseEvent<HTMLElement>) => {
                stopPropagation(e);
                onColorBarClick(e.currentTarget);
              } : undefined}
              data-testid="vehicle-color-bar"
              sx={{
                width: 6,
                alignSelf: 'stretch',
                // カードの上下パディングを打ち消してカード全高に伸ばす
                // (サンプルの色バーはカード直下にあり、上下いっぱいに出る)
                mt: `-${VEHICLE_CARD_PADDING_TOP}px`,
                mb: `-${VEHICLE_CARD_PADDING_BOTTOM}px`,
                borderRadius: 1,
                flexShrink: 0,
                p: 0,
                border: 'none',
                bgcolor: barColor,
                cursor: onColorBarClick && !colorDisabledReason ? 'pointer' : 'default',
                opacity: onColorBarClick && colorDisabledReason ? 0.5 : 1,
                // hover 強調は width を変えると行内が横に揺れるため、レイアウトに影響しない
                // transform(scaleX) で視覚的に太らせる（siblings は動かない）。
                transition: 'transform 0.1s',
                '&:hover': onColorBarClick && !colorDisabledReason ? { transform: 'scaleX(1.6)' } : undefined,
              }}
            />
          )}
          {expandToggle}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1, height: '100%', '& > * + *': { mt: '2px' } }}>
          {/* 1段目: 運送会社 + 車両名 (車両名はクリックで車両選択メニューを開く)
              運送会社名が長い場合に車両名が押し潰されないよう、運送会社は最大 45% 幅で
              clip 省略、車両名は残り幅 (flex 1 1 auto) を使う。両方 Tooltip で全文表示。 */}
          {/* disableInteractive が無いとツールチップ上にカーソルが乗った時点でツールチップが
              維持され、下の色変更ボタンに到達できない（依頼文の「思った通りに操作できない」の主因）。 */}
          <Box sx={{ display: 'flex', minWidth: 0, alignItems: 'center', gap: 0.5, fontSize: 13, lineHeight: 1, pt: '2px' }}>
            {showCourseNumber && (
              <Tooltip title={`コース番号 ${courseNumber}`} placement="top" arrow enterDelay={400} enterNextDelay={400} leaveDelay={0} disableInteractive>
                <Box
                  component="span"
                  data-testid="vehicle-course-number"
                  sx={{
                    flexShrink: 0,
                    minWidth: 24,
                    px: 0.5,
                    py: '2px',
                    borderRadius: 0.75,
                    bgcolor: 'hsl(var(--foreground))',
                    color: 'hsl(var(--background))',
                    fontSize: 10,
                    fontWeight: 800,
                    lineHeight: 1,
                    textAlign: 'center',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {courseNumber}
                </Box>
              </Tooltip>
            )}
            {vehicle.companyName && (
              <Tooltip title={vehicle.companyName} placement="top" arrow enterDelay={400} enterNextDelay={400} leaveDelay={0} disableInteractive>
                <Box
                  component="span"
                  sx={{
                    flex: '0 1 auto',
                    minWidth: 0,
                    maxWidth: '45%',
                    overflow: 'hidden',
                    textOverflow: 'clip',
                    whiteSpace: 'nowrap',
                    fontWeight: 700,
                    letterSpacing: '-0.01em',
                    color: 'hsl(var(--foreground))',
                  }}
                >
                  {vehicle.companyName}
                </Box>
              </Tooltip>
            )}
            {vehicle.companyName && (
              <Box component="span" sx={{ flexShrink: 0, color: 'hsl(var(--muted-foreground))', opacity: 0.6 }}>/</Box>
            )}
            <Tooltip title={vehicle.truckName || vehicle.vehicleNumber} placement="top" arrow enterDelay={400} enterNextDelay={400} leaveDelay={0} disableInteractive>
              <Box
                component={onTruckClick ? 'button' : 'span'}
                type={onTruckClick ? 'button' : undefined}
                onClick={onTruckClick ? (e: MouseEvent<HTMLElement>) => {
                  stopPropagation(e);
                  ReactGA.event('click', {
                    screen_name: screenName,
                    button_name: 'トラック変更',
                  });
                  onTruckClick(e.currentTarget);
                } : undefined}
                sx={{
                  // 伸ばすとカードの右半分まで反応してしまい、行を選べなくなる
                  flex: '0 1 auto',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'clip',
                  whiteSpace: 'nowrap',
                  fontWeight: vehicle.companyName ? 400 : 700,
                  color: unsetColorFor(vehicle.truckName || vehicle.vehicleNumber) ?? 'hsl(var(--foreground))',
                  bgcolor: 'transparent',
                  border: 'none',
                  p: 0,
                  textAlign: 'left',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '1px',
                  borderRadius: 0.5,
                  cursor: onTruckClick ? 'pointer' : 'default',
                  transition: 'background-color 120ms',
                  '&:hover': onTruckClick ? { bgcolor: 'hsl(var(--secondary))' } : undefined,
                }}
              >
                <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'clip', whiteSpace: 'nowrap' }}>
                  {vehicle.truckName || vehicle.vehicleNumber}
                </Box>
                {/* 選び直せることが hover するまで伝わらなかったので ▼ を出す
                    (サンプル VehicleInfo.tsx:208) */}
                {onTruckClick && (
                  <ArrowDropDown sx={{ fontSize: 14, color: 'hsl(var(--muted-foreground))', ml: '-2px', flexShrink: 0 }} />
                )}
              </Box>
            </Tooltip>
            {/* 名前とバッジの間の余白。ここはカードの一部なので、押すと行が選ばれる */}
            <Box data-testid="vehicle-name-spacer" sx={{ flex: '1 1 auto', minWidth: 0 }} />
            {/* 車両タイプと納品先数。表示項目のタグで出し分ける。
                行頭の名前が伸びても潰れないよう flexShrink: 0 で右端に固定する */}
            {showVehicleTypeTag && vehicle.truckKlass && (
              <Box component="span" sx={metaBadgeSx}>{vehicle.truckKlass}</Box>
            )}
            {/* 確定と計画無も車両の状態なので、納品件数と同じ帯に並べる。
                計画が無い車両は納品件数が出ないため、両者が同じ位置を取り合わない */}
            {fixed && (
              <Box
                component="span"
                sx={{
                  ...metaBadgeSx,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.25,
                  bgcolor: 'success.light',
                  color: 'success.contrastText',
                }}
              >
                🔒確定
              </Box>
            )}
            {noPlan && (
              <Box
                component="span"
                sx={{
                  ...metaBadgeSx,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.25,
                  bgcolor: 'hsl(var(--destructive) / 0.12)',
                  color: 'hsl(var(--destructive))',
                }}
              >
                計画無
                {onDeleteShift && (
                  <Box
                    component="button"
                    type="button"
                    aria-label="この勤務を削除"
                    disabled={!!deleteShiftDisabledReason}
                    // disabled 要素は MUI Tooltip の hover を拾えないので素の title で出す
                    // (ConvenienceDispatchToolbar.tsx:551 と同じ規則)
                    title={deleteShiftDisabledReason}
                    onClick={(e: MouseEvent<HTMLElement>) => {
                      // 破壊的操作なので行選択・ドラッグを巻き込まない
                      stopPropagation(e);
                      ReactGA.event('click', {
                        screen_name: screenName,
                        button_name: '勤務削除',
                      });
                      onDeleteShift();
                    }}
                    onPointerDown={stopPropagation}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      border: 'none',
                      p: 0,
                      bgcolor: 'transparent',
                      color: 'inherit',
                      cursor: deleteShiftDisabledReason ? 'default' : 'pointer',
                      opacity: deleteShiftDisabledReason ? 0.4 : 1,
                    }}
                  >
                    <CloseRounded sx={{ fontSize: 10 }} />
                  </Box>
                )}
              </Box>
            )}
            {/* 制約違反。絶対配置で 1 段目に重ねていたときは積込・納品バッジを隠していた。
                同じ段に並べ、狭いときに縮む・消えるのは積込・納品の側にする
                （表示優先度は 制約 ＞ 積込 ＝ 納品） */}
            {alert && (
              <Tooltip
                title={
                  <Box sx={{ py: 0.25 }}>
                    <Box sx={{ fontWeight: 700, fontSize: 11, mb: 0.5 }}>制約違反</Box>
                    {alert.reasons.map((reason) => (
                      <Box key={reason} sx={{ fontSize: 11, lineHeight: 1.5 }}>・{reason}</Box>
                    ))}
                  </Box>
                }
                placement="top"
                enterDelay={150}
              >
                <Box
                  component="span"
                  data-testid="vehicle-alert-badge"
                  onClick={stopPropagation}
                  sx={{
                    ...metaBadgeSx,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.4,
                    bgcolor: 'hsl(var(--status-error))',
                    color: 'hsl(var(--status-error-foreground))',
                    cursor: 'help',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <WarningAmberRounded sx={{ fontSize: 12, flexShrink: 0 }} />
                  {alert.label}
                </Box>
              </Tooltip>
            )}
            {/* 積込・納品は制約より優先度が低いので、狭いときはこちらが縮む */}
            {showPickupTag && pickupCount > 0 && (
              <CountTag
                testId="pickup-tag"
                label="積込"
                count={pickupCount}
                sx={{ bgcolor: 'hsl(142 70% 45% / 0.12)', color: 'hsl(142 70% 30%)' }}
              />
            )}
            {showDeliveryTag && destinationCount > 0 && (
              <CountTag
                testId="delivery-tag"
                label="納品"
                count={destinationCount}
                sx={{ bgcolor: 'hsl(var(--primary) / 0.1)', color: 'hsl(var(--primary))' }}
              />
            )}
            {/* 回転バッジは 1 段目の右端。サンプル (VehicleInfo.tsx:261-311) と同じ位置で、
                右カラムに置いていた頃より容量・粗利の行に幅を譲れる */}
            {showRotation && rotationNumber >= 2 && (
              <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }} onClick={stopPropagation}>
                <Box
                  component="span"
                  sx={{
                    borderRadius: 0.5,
                    bgcolor: 'hsl(var(--accent))',
                    px: 0.75,
                    py: 0.25,
                    fontSize: 10,
                    fontWeight: 600,
                    lineHeight: 1,
                    color: 'hsl(var(--accent-foreground))',
                  }}
                >
                  {rotationNumber}回転目
                </Box>
                {rotationEditable && canDeleteRound && enabled && (
                  <Box
                    component="button"
                    type="button"
                    disabled={!!deleteRoundDisabledReason}
                    title={deleteRoundDisabledReason}
                    onClick={(e) => {
                      stopPropagation(e);
                      ReactGA.event('click', {
                        screen_name: screenName,
                        button_name: '回転削除',
                      });
                      onDeleteRound?.();
                    }}
                    aria-label="この回転を削除"
                    sx={{
                      display: 'inline-flex',
                      height: 16,
                      width: 16,
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 0.5,
                      border: 'none',
                      bgcolor: 'transparent',
                      color: 'hsl(var(--muted-foreground))',
                      cursor: deleteRoundDisabledReason ? 'default' : 'pointer',
                      opacity: deleteRoundDisabledReason ? 0.5 : 1,
                      transition: 'background-color 150ms, color 150ms',
                      '&:hover': deleteRoundDisabledReason ? undefined : {
                        bgcolor: 'hsl(var(--destructive) / 0.1)',
                        color: 'hsl(var(--destructive))',
                      },
                    }}
                  >
                    <CloseRounded sx={{ fontSize: 12 }} />
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {/* 2段目: 車番 + ドライバー名 (両方クリック可)。
              字の大きさは行に置く（欄ごとに書くと車番とドライバーで食い違う）。
              11px はサンプル lovable の欄に寄せた値 */}
          <Box sx={{ display: 'flex', minWidth: 0, alignItems: 'center', gap: 1, fontSize: 11 }}>
            <Box
              component={onPlateNumberClick ? 'button' : 'span'}
              type={onPlateNumberClick ? 'button' : undefined}
              onClick={onPlateNumberClick ? (e: MouseEvent<HTMLElement>) => {
                stopPropagation(e);
                ReactGA.event('click', {
                  screen_name: screenName,
                  button_name: '車番変更',
                });
                onPlateNumberClick(e.currentTarget);
              } : undefined}
              sx={{
                overflow: 'hidden',
                textOverflow: 'clip',
                whiteSpace: 'nowrap',
                // 稼働時間に押されて縮むと最小幅の意味が無くなる。時刻は溢れてよい
                // (サンプルも溢れている)
                minWidth: onPlateNumberClick ? PLATE_NUMBER_MIN_WIDTH : undefined,
                flexShrink: 0,
                color: plateNumber ? 'hsl(var(--foreground))' : 'hsl(var(--muted-foreground) / 0.7)',
                ...(onPlateNumberClick
                  ? fieldSxFor(plateNumber)
                  : { bgcolor: 'transparent', border: 'none', p: 0 }),
              }}
            >
              {plateNumber || '車番未設定'}
            </Box>
            <Box
              component={onDriverClick ? 'button' : 'span'}
              type={onDriverClick ? 'button' : undefined}
              onClick={onDriverClick ? (e: MouseEvent<HTMLElement>) => {
                stopPropagation(e);
                ReactGA.event('click', {
                  screen_name: screenName,
                  button_name: 'ドライバー変更',
                });
                onDriverClick(e.currentTarget);
              } : undefined}
              sx={{
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'clip',
                whiteSpace: 'nowrap',
                textAlign: 'left',
                color: unsetColorFor(currentDriverName || DRIVER_UNSET_LABEL) ?? 'hsl(var(--foreground))',
                ...(onDriverClick
                  ? { ...fieldSxFor(currentDriverName), flex: '1 1 auto' }
                  : { bgcolor: 'transparent', border: 'none', p: 0 }),
              }}
            >
              {currentDriverName || DRIVER_UNSET_LABEL}
            </Box>
            {showDriverShift && shiftDateLabel && (
              <Tooltip title={shiftTooltip} placement="top" arrow enterDelay={400} enterNextDelay={400} leaveDelay={0} disableInteractive>
                <Box
                  component="span"
                  sx={{
                    flexShrink: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'clip',
                    whiteSpace: 'nowrap',
                    fontSize: 11,
                    fontVariantNumeric: 'tabular-nums',
                    color: 'hsl(var(--muted-foreground))',
                  }}
                >
                  {shiftDateLabel.label}
                </Box>
              </Tooltip>
            )}
          </Box>

          {/* 3 段目: 積載量 + 粗利 + ＋回転。3 つとも隠す画面では段ごと描かない
              （空の段を残すと、カード下端に何も入らない帯が残る） */}
          {(showCapacity || showGrossProfit || showAddRoundButton) && (
          <Box
            data-testid="vehicle-card-metrics-row"
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, fontSize: 12, lineHeight: 1 }}
          >
            {showCapacity && withCapacityTooltip(
              <Box
                component="span"
                sx={{
                  fontFamily: 'monospace',
                  fontVariantNumeric: 'tabular-nums',
                  // 同じ行に粗利と「＋回転」が並ぶ。左パネルを狭めたときに折り返らせない
                  // （実測: 行幅 130px で 2 行になり段の高さが 20 → 24px に伸びた）
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  fontWeight: enabled && isOverloaded ? 700 : 400,
                  color: enabled && isOverloaded ? 'hsl(var(--status-error))' : 'hsl(var(--foreground))',
                }}
              >
                {activeMetric === 'quantity'
                  ? `${quantityValueText(load.cases)} / ${maxCases != null ? quantityValueText(maxCases) : '--'}cs`
                  : activeMetric === 'weight'
                    ? `${loadWeightValueText(load.kg)} / ${maxKg != null ? loadWeightValueText(maxKg) : '--'}kg`
                    : `${volumeValueText(load.m3)} / ${maxM3 != null ? volumeValueText(maxM3) : '--'}m³`}
              </Box>,
            )}
            {showGrossProfit && (
              <Tooltip
                // 粗利だけだと「請求が高いのか支払が安いのか」が読めない
                // (サンプル VehicleInfo.tsx:487-497)
                title={
                  <Box sx={{ py: 0.25, minWidth: 120 }} data-testid="gross-profit-breakdown">
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, lineHeight: 1.6 }}>
                      <span>請求</span><span>{yen(tariffTotals?.billingYen ?? 0)}</span>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, lineHeight: 1.6 }}>
                      <span>支払</span><span>{yen(tariffTotals?.paymentYen ?? 0)}</span>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, lineHeight: 1.6, fontWeight: 700, borderTop: '1px solid hsl(var(--border))', mt: 0.25, pt: 0.25 }}>
                      <span>粗利</span><span>{yen(grossProfitYen)}</span>
                    </Box>
                  </Box>
                }
                placement="top" arrow
                enterDelay={400} enterNextDelay={400}
                leaveDelay={0} disableInteractive
              >
                <Box
                  component="span"
                  sx={{
                    // 「＋回転」のすぐ左に寄せる。絶対配置で右下に浮かせていたときは
                    // 積載バーの上に離れて出ていた
                    ml: 'auto',
                    // ボタンとラベルは縮めない (幅を奪い合うとラベルが折り返る)。
                    // 狭いときは粗利だけが縮んで省略される
                    flexShrink: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textAlign: 'right',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: 11,
                    fontVariantNumeric: 'tabular-nums',
                    fontWeight: 600,
                    lineHeight: 1,
                    // サンプル (route-planner VehicleInfo) 準拠で損益を色だけで一目で判別できるよう、
                    // 正値は緑・負値は赤にする（ユーザー判断でサンプルの緑を採用）。
                    color: grossProfitYen < 0 ? 'hsl(var(--status-error))' : 'hsl(142 65% 32%)',
                  }}
                >
                  {grossProfitText}
                </Box>
              </Tooltip>
            )}
            {showAddRoundButton && (
              <Box
                component="button"
                type="button"
                onClick={(e) => {
                  stopPropagation(e);
                  ReactGA.event('click', {
                    screen_name: screenName,
                    button_name: '回転追加',
                  });
                  onAddRound?.();
                }}
                aria-label="回転を追加"
                sx={{
                  display: 'inline-flex',
                  height: 20,
                  alignItems: 'center',
                  gap: 0.5,
                  borderRadius: 1,
                  border: '1px dashed hsl(var(--input))',
                  px: 0.75,
                  fontSize: 10,
                  fontWeight: 600,
                  color: 'hsl(var(--muted-foreground))',
                  bgcolor: 'transparent',
                  cursor: 'pointer',
                  // 粗利は絶対配置 (下記) で行から外したため通常はこのボタンを押し縮めないが、
                  // 将来また同じ行に幅を取る表示が増えた場合の再発防止として、ボタン自体は
                  // 縮まず (flexShrink: 0) ラベルは折り返さない (whiteSpace: nowrap) を維持する。
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  transition: 'background-color 150ms',
                  '&:hover': { bgcolor: 'hsl(var(--secondary))' },
                }}
              >
                {/* 表示テキストはサンプル (route-planner VehicleInfo) 準拠で「回転」に短縮
                    (元は「回転追加」で2文字分幅を取り、粗利の表示余地を圧迫していた)。
                    aria-label と GA の button_name は計測継続性のため変えない。 */}
                <AddRounded sx={{ fontSize: 12 }} />
                回転
              </Box>
            )}
          </Box>
          )}
        </Box>

      </Box>
      {/* 容量バーはカードを基準に置く。本文カラムを基準にすると 3 段が
          カラムの高さを超えたときに数値の上へ乗る（実測で確認）。
          left / right / bottom は vehicleCardStyles の実測値に従う。
          width の max(6px, …) は積載 0 でも芯を残すため（消えると
          「バーが無い車両」と「積載ゼロの車両」が見分けられない） */}
      {showCapacity && withCapacityTooltip(
        <Box
          data-testid="capacity-bar"
          sx={{
            position: 'absolute',
            left: barLeftInset,
            // right ではなく width で止める。全幅だと「＋回転」ボタンに届く
            width: capacityBarTrackWidth(barLeftInset),
            bottom: CAPACITY_BAR_INSET.bottom,
            height: CAPACITY_BAR_HEIGHT,
            overflow: 'hidden',
            borderRadius: 999,
            bgcolor: 'hsl(var(--foreground) / 0.14)',
            border: '1px solid hsl(var(--border))',
            // ツールチップを出すときだけ hover を拾う。出す内容が無いのに当たり判定を
            // 増やすと、5px の帯の上でだけカーソルが変わって押せる要素のように見える
            pointerEvents: capacityTooltipRows.length === 0 ? 'none' : 'auto',
          }}
        >
          <Box
            sx={{
              height: '100%',
              borderRadius: 999,
              bgcolor: enabled
                ? isOverloaded
                  ? 'hsl(var(--status-error-accent))'
                  : 'hsl(217 91% 55%)'
                : 'hsl(var(--muted-foreground) / 0.5)',
              width: capacityBarWidth(gaugeWidth),
              transition: 'width 200ms',
            }}
          />
        </Box>,
      )}
    </Box>
  );
});

VehicleInfoView.displayName = 'VehicleInfoView';
